import { useMutation as reactQueryUseMutation, UseMutationOptions, MutationKey } from '@tanstack/react-query';

import useConfig from 'hooks/useConfig';
import { getErrorWithMessage } from 'utils';

import { ApiMethods, CuredApiPaths } from '../models/enums';
import { PathWithID } from '../models/types';

import useGetAxiosInstance from './useGetAxiosInstance';

const isPathWithID = (path: CuredApiPaths | PathWithID | string): path is PathWithID =>
    Boolean((path as PathWithID)?.path);

/**
 * A wrapper around the react-query hook of the same name.
 * For more details see: https://react-query-v3.tanstack.com/reference/useMutation
 * @param mutationKey a unique id for the mutation used for caching
 * @param path the API path
 * @param mutateOptions options for the mutation
 * @returns the react-query mutation
 */
export default function useMutation<TVariables = unknown, TData = unknown, TError = Error, TContext = unknown>(
    mutationKey: string | MutationKey,
    // todo - see if there is a way to make this type stronger
    path: CuredApiPaths | PathWithID | string,
    mutateOptions: UseMutationOptions<TData, TError, TVariables, TContext>,
    method: ApiMethods.DELETE | ApiMethods.PATCH | ApiMethods.POST | ApiMethods.PUT = ApiMethods.POST,
    authenticated: boolean = true,
    headers?: Record<string, string>
) {
    const useConfigQuery = useConfig();

    if (!useConfigQuery.config) {
        throw new Error(
            'You must mock useMutation hooks in unit tests. See the docs for details: https://github.com/Cured-Health/web-app/blob/development/frontend/docs/testing.md#how-to-mock-usequery-hooks'
        );
    }

    const {
        config: { API_URL },
    } = useConfig();
    const getApiInstance = useGetAxiosInstance(method, { headers }, authenticated);

    const normalizedKey: MutationKey = typeof mutationKey === 'string' ? [mutationKey] : mutationKey;

    const mutationResult = reactQueryUseMutation<TData, TError, TVariables, TContext>({
        mutationKey: normalizedKey,
        mutationFn: async (body: TVariables): Promise<TData> => {
            const apiInstance = await getApiInstance();
            const url = isPathWithID(path) ? `${API_URL}${path.url}` : path;

            try {
                switch (method) {
                    case ApiMethods.DELETE:
                        return (await apiInstance.delete(url)).data;
                    case ApiMethods.POST:
                        return (await apiInstance.post(url, body)).data;
                    case ApiMethods.PUT:
                        return (await apiInstance.put(url, body)).data;
                    case ApiMethods.PATCH:
                        return (await apiInstance.patch(url, body)).data;
                    default:
                        throw new Error(`Unsupported HTTP method: ${method}`);
                }
            } catch (error) {
                const errorWithMessage = getErrorWithMessage(error);
                throw errorWithMessage;
            }
        },
        ...mutateOptions,
    });

    return mutationResult;
}
